import { isEqual } from 'lodash';
import React, { Component } from 'react';
import { withRouter, Link } from 'react-router-dom';
import { connect } from 'react-redux';
import { setAccountLink } from '../../../redux/actions';
import analyticService from '../../../services/analytics';
import Flag from '../../base/flag/flag';

import { getNetworkQueryParam } from '../../base/common/utils';

import { dateService } from '../../../services/date';
import dashboardService from './dashboard-service';
import { DateRangeDropdown, PubDashNetworks } from '../../base/megatable/models';
import { paymentsService } from '../../base/account/payments-service';

import EarningsGraph from './earnings-graph';
import LinksGraph from './links-graph';
import TotalsHero from './totals-hero';
import Barometers from './barometers';
import Icon from '../../ui/icon';

import './dashboard.scss';

class PublisherDashboard extends Component {
  constructor(props) {
    super(props);

    this.AnalyticService = analyticService;
    this.dateService = dateService;
    this.service = dashboardService;

    this.pubId = this.props.match.params.publisherId;
    this.presetRanges = this.dateService.getPresetDateRanges();
    this.rangeDropdownItems = new DateRangeDropdown().items;

    this.defaultStat = {
      clicks: 0,
      publisher_total_earnings: 0,
      publisher_epc: 0,
      total_attributed_revenue: 0,
      count_per_date: 0,
    };

    this.state = {
      networks: new PubDashNetworks().defaultNetworks,
      loading: true,
      showNoResult: false,
      graphDataByRange: {
        sevenDay: [],
        thisMonth: [],
        lastMonth: [],
        halfYear: [],
        thisYear: [],
        multiYear: [],
      },
      projectionsByRange: {
        sevenDay: {},
        thisMonth: {},
        lastMonth: {},
        halfYear: {},
        thisYear: {},
        multiYear: {},
      },
    };
  }

  componentDidUpdate(prevProps, prevState) {
    if (!isEqual(prevState.networks, this.state.networks)) {
      this.setState({ loading: true }, () => {
        this.getEarningsStats();
      });
    }

    if (!isEqual(this.props.flags, prevProps.flags)) {
      if (this.canGetAccountLink()) {
        this.getAccountLink();
      } else {
        this.props.setAccountLink({});
      }
    }
  }

  componentDidMount() {
    this.AnalyticService.trackEvent('Dashboard', 'Load', null);
    this.getEarningsStats();

    if (this.props.flags && this.canGetAccountLink()) {
      this.getAccountLink();
    } else {
      this.props.setAccountLink({});
    }
  }

  getEarningsStats() {
    const dayRange = this.presetRanges.last2Months;
    const monthRange = this.presetRanges[this.getMonthRange()];
    const yearRange = this.presetRanges.multiYear;

    const queries = [
      this.service.getStatsByDay(this.pubId, this.getRequestParams(dayRange)),
      this.service.getStatsByMonth(this.pubId, this.getRequestParams(monthRange)),
      this.service.getStatsByYear(this.pubId, this.getRequestParams(yearRange)),
    ];

    Promise.all(queries)
      .then(results => {
        const dailyData = results[0];
        const monthlyData = results[1];
        const yearlyData = results[2];
        const todayStat = dailyData[dailyData.length - 1];

        const graphDataByRange = {
          sevenDay: this.processGraphData(dailyData, 'sevenDay'),
          halfYear: this.processGraphData(monthlyData, 'halfYear'),
          thisMonth: this.processGraphData(dailyData, 'thisMonth'),
          lastMonth: this.processGraphData(dailyData, 'lastMonth'),
          thisYear: this.processGraphData(monthlyData, 'thisYear'),
          multiYear: yearlyData ? this.processGraphData(yearlyData, 'multiYear') : [],
        };
        const projectionsByRange = {
          sevenDay: this.getProjections(graphDataByRange.sevenDay, 'sevenDay'),
          halfYear: this.getProjections(graphDataByRange.halfYear, 'halfYear', todayStat),
          thisMonth: this.getProjections(graphDataByRange.thisMonth, 'thisMonth'),
          lastMonth: {},
          thisYear: this.getProjections(graphDataByRange.thisYear, 'thisYear', todayStat),
          multiYear: yearlyData
            ? this.getProjections(graphDataByRange.multiYear, 'multiYear', todayStat)
            : {},
        };

        this.setState({
          loading: false,
          showNoResult: false,
          graphDataByRange,
          projectionsByRange,
        });
      })
      .catch(() => {
        this.setState({ loading: false, showNoResult: true });
      });
  }

  getMonthRange() {
    // Use yearToDate if current month is greater than June
    const currentMonth = new Date().getMonth();
    return currentMonth > 5 ? 'thisYear' : 'halfYear';
  }

  getRequestParams(dateRange) {
    return {
      date_from: this.dateService.formatDate(dateRange.startDate),
      date_to: this.dateService.formatDate(dateRange.endDate),
      networks: getNetworkQueryParam(this.state.networks),
    };
  }

  /* Data manipulation */
  processGraphData(stats, rangeType) {
    /*
     * rangeType: sevenDay|thisMonth|lastMonth|halfYear|thisYear|multiYear
     * Generate date labels and match them to stats
     */
    const rangeObject = this.rangeDropdownItems.find(item => item.name === rangeType);
    const dateRange = this.presetRanges[rangeObject.name];
    const timeUnit = rangeObject.unit;

    const dateLabels = this.service.generateDatesForRange(
      dateRange.startDate,
      dateRange.endDate,
      timeUnit,
      rangeType,
    );
    const statsWithLabels = this.matchStatsToLabels(stats, dateLabels, rangeType);

    return this.service.formatDateLabels(statsWithLabels, timeUnit);
  }

  matchStatsToLabels(data, dateLabels, rangeType) {
    const compare = (stats, dateLabel) => {
      if (rangeType === 'multiYear') {
        return stats.event_year === dateLabel;
      }
      if (['halfYear', 'thisYear'].includes(rangeType)) {
        return stats.event_month === dateLabel;
      }
      return stats.event_date === dateLabel;
    };

    return dateLabels.map(dateLabel => {
      // Match date label to the most relevant stat or default to empty stat
      const relevantStats = data.filter(stat => compare(stat, dateLabel));
      let matchedStat = relevantStats[0];

      // If there are multiple matches, look for the most recent data point (by date)
      if (relevantStats.length > 1) {
        relevantStats.forEach(stat => {
          const statIsNewer =
            this.dateService.compareDates(matchedStat.event_month, stat.event_month) > 0;
          if (statIsNewer) matchedStat = stat;
        });
      }

      const stats = matchedStat || this.defaultStat;
      return { dateLabel, stats };
    });
    /*
     * [{dateLabel: '2019-10-01', stats: {clicks: 8000, ...}}, ...]
     */
  }

  getTimeElapsed(timeUnit) {
    let timeElapsed;
    if (timeUnit === 'day') {
      timeElapsed = this.dateService.dayElapsed(true);
    } else if (timeUnit === 'month') {
      timeElapsed = this.dateService.monthElapsed(true);
    } else {
      timeElapsed = this.dateService.yearElapsed(true);
    }
    return timeElapsed;
  }

  getProjections(stats, rangeType) {
    // Return projected stats for the current day/month (last data point).
    const projections = {};
    const fieldNames = ['clicks', 'publisher_total_earnings', 'total_attributed_revenue'];
    const stat = stats[stats.length - 1].stats || {}; // Project for the last data point
    const timeUnit = this.rangeDropdownItems.find(item => item.name === rangeType).unit;

    const parseValue = (fieldName, value) => {
      if (!value) return 0;
      return fieldName === 'clicks' ? parseInt(value) : parseFloat(value);
    };

    fieldNames.forEach(fieldName => {
      const value = parseValue(fieldName, stat[fieldName]);
      let projectedValue = value / this.getTimeElapsed(timeUnit);
      projectedValue =
        fieldName === 'clicks' ? Math.round(projectedValue) : projectedValue.toFixed(2);
      projections[fieldName] = projectedValue;
    });

    /*
     * {
     *   publisher_total_earnings: projected,
     *   clicks: projected,
     *   total_attributed_revenue: projected,
     * }
     */
    return projections;
  }

  canGetAccountLink() {
    return this.props.flags.billingFeatures && paymentsService.checkIfBillingUser();
  }

  async getAccountLink() {
    try {
      const res = await paymentsService.getAccountLink(this.props.match.params.publisherId);
      this.props.setAccountLink(res.data.data[0]);
    } catch (e) {
      console.log(e);
    }
  }

  getPaymentsBanner() {
    const status = paymentsService.getStatus(this.props.accountLink);

    const title =
      status === 'unconnected'
        ? 'Connect a Payment Method'
        : ['disabled', 'descriptive-error'].includes(status)
        ? 'Check your Payment Settings'
        : '';

    const location = !this.props.flags.paymentsNavitem
      ? `/v2/publisher/${this.props.match.params.publisherId}/account-settings/payments/payment-setup`
      : `/v2/publisher/${this.props.match.params.publisherId}/payments/payment-setup`;

    const paymentsLink = <Link to={location}>Payment Settings</Link>;

    const description =
      status === 'unconnected' ? (
        <>
          <span>Go to </span>
          {paymentsLink}
          <span> to setup your payment details so you can receive payouts from Narrativ.</span>
        </>
      ) : ['disabled', 'descriptive-error'].includes(status) ? (
        <>
          <span>There was an issue with your payment setup. Visit your </span>
          {paymentsLink}
          <span> for more details.</span>
        </>
      ) : (
        ''
      );

    return (
      <div className="payments-banner">
        <div>
          <div className="header">
            <div className="alert-icon-container">
              <Icon iconName="filledAlert" size={16} />
            </div>
            <span>{title}</span>
          </div>
          <div className="description">{description}</div>
        </div>
        <Link to={location}>
          <button>Go To Payment Setup</button>
        </Link>
      </div>
    );
  }

  render() {
    const paymentsBanner =
      this.props.accountLink && paymentsService.showAlertIcon(this.props.accountLink)
        ? this.getPaymentsBanner()
        : null;

    return (
      <div className="publisher-dashboard fs-unmask">
        <Flag name="billingFeatures">{paymentsBanner}</Flag>
        <TotalsHero pubId={this.pubId} />
        <EarningsGraph
          pubId={this.pubId}
          loading={this.state.loading}
          showNoResult={this.state.showNoResult}
          graphDataByRange={this.state.graphDataByRange}
          projectionsByRange={this.state.projectionsByRange}
          rangeDropdownItems={this.rangeDropdownItems}
          networks={this.state.networks}
          updateNetworks={networks => this.setState({ networks })}
        />
        <Barometers pubId={this.pubId} networks={this.state.networks} />
        <LinksGraph
          pubId={this.pubId}
          loading={this.state.loading}
          showNoResult={this.state.showNoResult}
          graphDataByRange={this.state.graphDataByRange}
          rangeDropdownItems={this.rangeDropdownItems}
        />
      </div>
    );
  }
}

const mapStateToProps = state => {
  return {
    accountLink: state.accountLink,
  };
};

const mapDispatchToProps = dispatch => {
  return {
    setAccountLink: accountLink => dispatch(setAccountLink(accountLink)),
  };
};

export default withRouter(connect(mapStateToProps, mapDispatchToProps)(PublisherDashboard));
